/**
 * PhysiologicalWaveformEngine — v10.3.0
 *
 * Núcleo compartido que:
 *   1. Lee `patient_current_state` (vitals + patientState) UNA VEZ.
 *   2. Expone helpers de tiempo/fase para que ECG, Pleth, Resp y Pressure
 *      compartan la misma fisiología (evita señales fantasma independientes).
 *   3. Implementa el `Pulse Transit Delay` para que la Pleth aparezca ~200ms
 *      DESPUÉS del complejo QRS del ECG.
 *
 * ARQUITECTURA:
 *   patient_current_state
 *          │
 *   PhysiologicalWaveformEngine  ── clock/PTT/quality
 *     │            │           │             │
 *     ECGGenerator PlethGen   RespGen   PressureGen
 *
 * IMPORTANTE:
 * - Cada generador SÓLO usa los parámetros fisiológicos relevantes para
 *   su señal. No hay cross-contamination (SpO₂ no mueve el baseline del
 *   ECG, la FR no cambia la morfología del ECG, etc.).
 * - Baselines siempre estables: la amplitud escala la señal SOBRE el eje,
 *   no lo desplaza.
 */
import { resolveNoiseProfile } from './artifacts';

// Retraso fisiológico entre despolarización ventricular (QRS) y la
// llegada del pulso al dedo (Pleth). Valor clínico típico: 180-250ms.
export const PULSE_TRANSIT_DELAY_MS = 200;

// Ritmos que no producen pulso periférico (Pleth plana)
export const PULSELESS_RHYTHMS = new Set(['fibrilacion_ventricular', 'asistolia', 'aesp']);

// Ritmos con actividad ventricular caótica (sin QRS organizado)
export const CHAOTIC_RHYTHMS = new Set(['fibrilacion_ventricular']);

// Ritmos con actividad ausente
export const FLATLINE_RHYTHMS = new Set(['asistolia']);

/**
 * Determina el ritmo efectivo. Si el escenario/instructor no lo define,
 * lo derivamos de FC (mantiene retrocompatibilidad).
 */
export function resolveRhythm(vitals = {}, patientState = null) {
  const fc = vitals.fc || 0;
  if (patientState === 'paro' && !vitals.ritmo_ecg) return 'asistolia';
  if (vitals.ritmo_ecg) return vitals.ritmo_ecg;
  if (!fc) return 'asistolia';
  if (fc > 150) return 'taquicardia_regular_angosto';
  if (fc > 100) return 'taquicardia_sinusal';
  if (fc < 60) return 'bradicardia_sinusal';
  return 'sinusal';
}

/**
 * Devuelve la marca de tiempo del último QRS antes de `nowMs`.
 * Para ritmos irregulares (FA/AESP) usamos un jitter determinista.
 */
export function lastQrsTime(nowMs, hr, rhythm) {
  if (!hr || hr <= 0) return null;
  if (FLATLINE_RHYTHMS.has(rhythm)) return null;
  if (CHAOTIC_RHYTHMS.has(rhythm)) return null; // sin QRS organizado
  const period = 60000 / hr;
  return Math.floor(nowMs / period) * period;
}

/**
 * Fase [0..1) dentro del ciclo cardíaco actual (para ECG).
 * Para ritmos irregulares el llamador debe usar su propia lógica.
 */
export function beatPhase(nowMs, hr) {
  const period = 60000 / Math.max(20, hr || 70);
  return ((nowMs % period) + period) % period / period;
}

/**
 * Resuelve la configuración global de señal a partir del estado del
 * paciente. Todos los generadores usan esta salida como fuente única.
 */
export function resolveSignalConfig(vitals = {}, patientState = null) {
  const rhythm = resolveRhythm(vitals, patientState);
  const noise = resolveNoiseProfile(vitals);

  // Amplitudes (defaults 1.0). Escalan la magnitud de la señal SOBRE el
  // eje, nunca mueven el baseline.
  const ecgAmp = clamp(numOr(vitals.ecg_amplitude, 1.0), 0.2, 2.0);
  const plethAmp = clamp(numOr(vitals.pleth_amplitude, 1.0), 0.2, 2.0);
  const respAmp = clamp(numOr(vitals.respiratory_amplitude, 1.0), 0.2, 2.0);

  // Perfusión (0..1) — atenúa amplitud del pulso pero no la SpO₂ numérica.
  // Si el instructor no la especifica, la derivamos del cuadro clínico.
  let perfusion = numOr(vitals.perfusion, null);
  if (perfusion == null) {
    if (patientState === 'paro' || PULSELESS_RHYTHMS.has(rhythm)) perfusion = 0;
    else if (patientState === 'critico') perfusion = 0.4;
    else if ((vitals.pa_sistolica || 120) < 80) perfusion = 0.5;
    else if ((vitals.spo2 || 98) < 85) perfusion = 0.6;
    else perfusion = 1.0;
  }
  perfusion = clamp(perfusion, 0, 1);

  return {
    rhythm,
    hr: vitals.fc || 0,
    pulseRate: numOr(vitals.pulse_rate, vitals.fc || 0),
    spo2: vitals.spo2 || null,
    paSys: vitals.pa_sistolica || null,
    paDia: vitals.pa_diastolica || null,
    rr: numOr(vitals.fr, 0),
    respPattern: vitals.patron_respiratorio || 'normal',
    ecgAmp,
    plethAmp,
    respAmp,
    perfusion,
    noise,          // { whiteAmp, baselineAmp, powerHumAmp, contactSeverity, signalAttenuation, quality }
    artifactType: vitals.artifact_type || null,
    patientState,
  };
}

function numOr(v, fallback) {
  return typeof v === 'number' && !Number.isNaN(v) ? v : fallback;
}
function clamp(v, min, max) { return Math.min(max, Math.max(min, v)); }
